import { useEffect, useState } from "react";
import { ArrowDown, ArrowRight, Leaf, Users, Tag, Star, Recycle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/Urls";

const CATEGORIES = [
  "Furniture",
  "Electronics",
  "Clothing",
  "Books & Media",
  "Home & Garden",
  "Kids & Baby",
  "Sporting Goods",
  "Musical Instruments",
];

const HeroSection = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [userCount, setUserCount] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadCount = () => {
      api
        .get("/api/public/stats/total-users")
        .then((res) => {
          if (!cancelled) {
            const count = Number(res.data?.count);
            setUserCount(Number.isFinite(count) ? count : null);
          }
        })
        .catch(() => {
          if (!cancelled) setUserCount(null);
        });
    };

    loadCount();
    window.addEventListener("focus", loadCount);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", loadCount);
    };
  }, []);

  const openAdminUsers = () => {
    // Navigate immediately. The Admin Users page performs the protected
    // request and shows its own loading state while data is fetched.
    navigate("/admin/users", {
      state: {
        initialScope: "all",
      },
    });
  };

  const stats = [
    {
      icon: Users,
      value: userCount == null ? "—" : String(userCount),
      label: "Active users",
      adminEntry: true,
    },
    { icon: Tag, value: "150+", label: "Live listings" },
    { icon: Star, value: "98%", label: "Feedback score" },
  ];

  return (
    <section className="relative overflow-hidden border-b border-border bg-background">
      <style>{`
        @keyframes hero-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes hero-blob-float { 0%, 100% { transform: translate(-50%, 0) scale(1); } 50% { transform: translate(-50%, -18px) scale(1.05); } }
        @keyframes hero-sway { 0%, 100% { transform: rotate(-6deg); } 50% { transform: rotate(4deg); } }
        @keyframes hero-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes hero-shimmer { from { transform: translateX(-130%) skewX(-12deg); } to { transform: translateX(230%) skewX(-12deg); } }

        .hero-anim { opacity: 0; animation: hero-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
        .hero-d1 { animation-delay: .08s; }
        .hero-d2 { animation-delay: .16s; }
        .hero-d3 { animation-delay: .24s; }
        .hero-d4 { animation-delay: .32s; }
        .hero-d5 { animation-delay: .4s; }
        .hero-d6 { animation-delay: .48s; }

        .hero-blob { animation: hero-blob-float 9s ease-in-out infinite; }
        .hero-tag { animation: hero-sway 4.5s ease-in-out infinite; transform-origin: top center; }

        .hero-ticker { overflow: hidden; -webkit-mask-image: linear-gradient(90deg, transparent, black 15%, black 85%, transparent); mask-image: linear-gradient(90deg, transparent, black 15%, black 85%, transparent); }
        .hero-ticker-track { width: max-content; animation: hero-marquee 28s linear infinite; }

        .hero-cta { position: relative; overflow: hidden; }
        .hero-cta::after { content: ""; position: absolute; inset: 0; background: linear-gradient(115deg, transparent, hsl(var(--primary-foreground) / 35%), transparent); transform: translateX(-130%) skewX(-12deg); }
        .hero-cta:hover::after { animation: hero-shimmer 0.85s ease forwards; }
        .hero-cta:hover { box-shadow: 0 20px 45px -14px hsl(var(--primary) / 55%); }

        @media (prefers-reduced-motion: reduce) {
          .hero-anim, .hero-blob, .hero-tag, .hero-ticker-track, .hero-cta::after { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      {/* Soft background glow */}
      <div className="hero-blob pointer-events-none absolute left-1/2 top-[-160px] h-[460px] w-[700px] -translate-x-1/2 rounded-full bg-primary/10 blur-[130px]" />
      {/* Faint dot texture, fading toward the edges */}
      <div className="pointer-events-none absolute inset-0 [background-image:radial-gradient(hsl(var(--foreground)/0.05)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_55%_45%_at_50%_0%,black,transparent)]" />

      <div className="container relative mx-auto px-4 py-16 text-center md:py-24">
        {/* Badge */}
        <div className="hero-anim mx-auto mb-7 flex w-fit items-center justify-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-5 py-2 text-sm font-medium text-primary backdrop-blur-sm">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          <Leaf className="h-4 w-4" />
          Reduce. Reuse. Re-deal.
        </div>

        {/* Heading */}
        <h1 className="hero-anim hero-d1 relative mx-auto max-w-4xl font-heading text-4xl font-bold leading-[1.08] tracking-tight text-foreground md:text-6xl lg:text-7xl">
          Give Items a{" "}
          <span className="relative inline-block text-primary">
            Second Life
            {/*<span className="absolute -bottom-2 left-1/2 h-[3px] w-3/4 -translate-x-1/2 rounded-full bg-primary/50" />

            {/* Signature: a hand-tied "reloved" price tag, true to the secondhand-market subject */}
            <span className="hero-tag pointer-events-none absolute -right-3 -top-9 hidden flex-col items-center sm:flex md:-right-7 md:-top-11">
              <svg width="14" height="30" viewBox="0 0 14 30" fill="none" className="text-border">
                <path d="M7 0 C7 12, 2 12, 2 24" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 3" />
              </svg>
              <span className="-mt-1 flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground shadow-sm">
                <Recycle className="h-3 w-3 text-primary" />
                Reloved
              </span>
            </span>
          </span>
        </h1>

        {/* Description */}
        <p className="hero-anim hero-d2 mx-auto mt-7 max-w-2xl text-base leading-7 text-muted-foreground md:text-lg">
          Buy, sell, donate or give away pre-loved items. Join a growing
          community that puts sustainability first.
        </p>

        {/* Scrolling category directory — real content, not decoration */}
        <div className="hero-anim hero-d3 hero-ticker mx-auto mt-6 max-w-xl">
          <div className="hero-ticker-track flex items-center gap-2.5 py-1">
            {[...CATEGORIES, ...CATEGORIES].map((category, index) => (
              <span
                key={`${category}-${index}`}
                className="whitespace-nowrap rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs font-medium text-muted-foreground"
              >
                {category}
              </span>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="hero-anim hero-d4 mt-9 flex flex-row items-center justify-center gap-3 sm:flex-row">
          <a
            href={token ? "#categories" : "/signin"}
            className="hero-cta group flex w-50 items-center justify-center gap-2 rounded-xl bg-primary px-2 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/10 transition-all duration-300 hover:-translate-y-0.5 sm:w-auto"
          >
            {token ? "Browse Categories" : "Sign In for Instant Upload Alerts"}
            {token ? (
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            ) : (
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            )}
          </a>

          <a
            href="/how-it-works"
            className="group flex w-50 items-center justify-center gap-2 rounded-xl border border-border bg-secondary/70 px-3 py-3 text-sm font-medium text-secondary-foreground backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-muted sm:w-auto"
          >
            How It Works
            <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-primary" />
          </a>
        </div>

        {/* Demo-only toggle so both CTA states are previewable here — remove when you paste this in 
        <button
          onClick={() => setToken(token ? null : "demo-token")}
          className="hero-anim hero-d5 mx-auto mt-4 block text-[11px] text-muted-foreground/70 underline decoration-dotted underline-offset-4 transition-colors hover:text-muted-foreground"
        >
          Preview toggle: viewing as {token ? "signed in" : "signed out"}
        </button>*/}

        {/* Stats */}
        <div className="hero-anim hero-d6 mx-auto mt-10 grid max-w-xl grid-cols-3 gap-px overflow-hidden rounded-2xl border border-border bg-border">
          {stats.map((stat) => {
            const Icon = stat.icon;
            if (stat.adminEntry) {
              return (
                <button
                  type="button"
                  key={stat.label}
                  onClick={openAdminUsers}
                  className="group flex flex-col items-center gap-1.5 bg-card px-3 py-5 transition-colors duration-300 hover:bg-muted/40"
                  aria-label="Registered users"
                >
                  <Icon className="h-4 w-4 text-primary/60 transition-transform duration-300 group-hover:scale-110 group-hover:text-primary" />
                  <p className="font-heading text-2xl font-bold text-primary md:text-3xl">
                    {/*stat.value*/}200+
                  </p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </button>
              );
            }

            return (
              <div
                key={stat.label}
                className="group flex flex-col items-center gap-1.5 bg-card px-3 py-5 transition-colors duration-300 hover:bg-muted/40"
              >
                <Icon className="h-4 w-4 text-primary/60 transition-transform duration-300 group-hover:scale-110 group-hover:text-primary" />
                <p className="font-heading text-2xl font-bold text-primary md:text-3xl">
                  {stat.value}
                </p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
